import apiClient from './client';

// ==================== Types ====================

/**
 * Context about the current page/entity the user is viewing
 */
export interface AdminChatContext {
  currentPage?: string;
  selectedEntityId?: string;
  selectedEntityType?: string;
}

/**
 * Request to send a message to the admin assistant
 */
export interface AdminChatRequest {
  message: string;
  conversationId?: string;
  context?: AdminChatContext;
}

/**
 * An action the user can execute with one click
 */
export interface SuggestedAction {
  actionId: string;
  type: string;
  label: string;
  description: string;
  params: Record<string, unknown>;
}

/**
 * A related page the user might want to navigate to
 */
export interface RelatedPage {
  label: string;
  route: string;
}

/**
 * Response from the admin assistant
 */
export interface AdminChatResponse {
  conversationId: string;
  response: string;
  suggestedAction?: SuggestedAction;
  relatedPages?: RelatedPage[];
  toolsUsed?: string[];
}

/**
 * Request to execute a suggested action
 */
export interface ExecuteActionRequest {
  actionId: string;
}

/**
 * Response from executing an action
 */
export interface ExecuteActionResponse {
  success: boolean;
  message?: string;
  errorMessage?: string;
}

/**
 * Admin assistant settings
 */
export interface AdminAssistantSettings {
  isEnabled: boolean;
  systemPromptAddition?: string;
  // Data access scopes
  canAccessCustomers: boolean;
  canAccessInvoices: boolean;
  canAccessPayments: boolean;
  canAccessServiceAreas: boolean;
  canAccessLeads: boolean;
  canAccessEquipment: boolean;
  canAccessInstallations: boolean;
  canAccessTickets: boolean;
  canAccessAddresses: boolean;
  canAccessBilling: boolean;
  canAccessTechnicians: boolean;
  canAccessSiteSurveys: boolean;
  canAccessServiceTiers: boolean;
  canAccessCommunicationLogs: boolean;
  canAccessNetwork: boolean;
  canAccessReports: boolean;
}

/**
 * Request to update admin assistant settings
 */
export interface UpdateAdminAssistantSettingsRequest {
  isEnabled: boolean;
  systemPromptAddition?: string;
  // Data access scopes
  canAccessCustomers?: boolean;
  canAccessInvoices?: boolean;
  canAccessPayments?: boolean;
  canAccessServiceAreas?: boolean;
  canAccessLeads?: boolean;
  canAccessEquipment?: boolean;
  canAccessInstallations?: boolean;
  canAccessTickets?: boolean;
  canAccessAddresses?: boolean;
  canAccessBilling?: boolean;
  canAccessTechnicians?: boolean;
  canAccessSiteSurveys?: boolean;
  canAccessServiceTiers?: boolean;
  canAccessCommunicationLogs?: boolean;
  canAccessNetwork?: boolean;
  canAccessReports?: boolean;
}

// ==================== API Functions ====================

/**
 * Send a message to the admin AI assistant
 */
export async function sendMessage(
  request: AdminChatRequest
): Promise<AdminChatResponse> {
  const response = await apiClient.post<AdminChatResponse>(
    '/admin/assistant',
    request
  );
  return response.data;
}

/**
 * Execute a suggested action from the assistant
 */
export async function executeAction(
  actionId: string
): Promise<ExecuteActionResponse> {
  const response = await apiClient.post<ExecuteActionResponse>(
    '/admin/assistant/execute-action',
    { actionId }
  );
  return response.data;
}

/**
 * Get admin assistant settings
 */
export async function getSettings(): Promise<AdminAssistantSettings> {
  const response = await apiClient.get<AdminAssistantSettings>(
    '/admin/assistant/settings'
  );
  return response.data;
}

/**
 * Update admin assistant settings
 */
export async function updateSettings(
  request: UpdateAdminAssistantSettingsRequest
): Promise<AdminAssistantSettings> {
  const response = await apiClient.put<AdminAssistantSettings>(
    '/admin/assistant/settings',
    request
  );
  return response.data;
}
